import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { Storefront as StorefrontIcon } from "../../icons";
import { Timeline as TimelineIcon } from "../../icons";
import { Payment as PaymentIcon } from "../../icons";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, db } from "../../firebase";
import { brandColors } from "../../theme";
import { T } from "../Translate";
import { onAccent } from "../supportChatStyles";
import StoresAppBar from "./StoresAppBar";
import { pageContentSx } from "../../pageLayout";

const steps = [
  {
    title: <T>Define tu catálogo</T>,
    body: <T>Carga productos, variantes y precios desde cualquier dispositivo.</T>,
    icon: <StorefrontIcon fontSize="large" sx={{ color: brandColors.accent }} />,
  },
  {
    title: <T>Configura tu experiencia</T>,
    body: <T>Personaliza secciones, idiomas y acciones principales en minutos.</T>,
    icon: <TimelineIcon fontSize="large" sx={{ color: brandColors.accent }} />,
  },
  {
    title: <T>Activa cobros</T>,
    body: <T>Conecta métodos de pago compatibles con Notiline y comienza a vender.</T>,
    icon: <PaymentIcon fontSize="large" sx={{ color: brandColors.accent }} />,
  },
];

const metrics = [
  {
    value: "10 min",
    label: <T>Tiempo promedio de configuración</T>,
    detail: <T>Prepara la estructura básica de tu tienda en pocos pasos guiados.</T>,
  },
  {
    value: "∞",
    label: <T>Actualizaciones</T>,
    detail: <T>Edita precios y textos las veces que necesites sin costos extra.</T>,
  },
  {
    value: "24/7",
    label: <T>Disponibilidad</T>,
    detail: <T>Tu catálogo permanece visible y listo para vender todo el día.</T>,
  },
];

const benefits = [
  {
    title: <T>Vende desde cualquier lugar</T>,
    body: <T>Gestiona pedidos desde la app móvil o la versión web sin herramientas externas.</T>,
  },
  {
    title: <T>Idiomas automáticos</T>,
    body: <T>Comparte enlaces que se adaptan al idioma que elija cada visitante.</T>,
  },
  {
    title: <T>Pagos integrados</T>,
    body: <T>Conecta tus métodos existentes y recibe notificaciones en tiempo real.</T>,
  },
];

const SECTION_ANCHOR_ID = "lanza-tu-tienda";

const SectionHeader = () => (
  <Box id={SECTION_ANCHOR_ID} sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
    <Typography variant="overline" sx={{ letterSpacing: 2, color: brandColors.accent }}>
      <T>Creación de tienda online</T>
    </Typography>
    <Typography
      variant="h3"
      sx={{ fontWeight: 700, mb: 2, fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" } }}
    >
      <T>Lanza tu tienda en Notiline</T>
    </Typography>
    <Typography variant="body1" sx={{ maxWidth: 640, mx: "auto", color: "text.secondary" }}>
      <T>Diseñamos un flujo guiado para que configures tu escaparate digital, publiques tus
      productos y gestiones pedidos sin salir de la plataforma.</T>
    </Typography>
    <Box sx={{ width: 88, height: 4, bgcolor: brandColors.accent, mx: "auto", mt: 4 }} />
  </Box>
);

const StoresSection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(true);
  const [storeError, setStoreError] = useState(null);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setStores([]);
      setLoadingStores(false);
      setStoreError(<T>Debes iniciar sesión para ver tus tiendas.</T>);
      return;
    }

    const unsubscribe = db
      .collection("stores")
      .where("ownerId", "==", user.uid)
      .onSnapshot(
        (snapshot) => {
          const userStores = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          setStores(userStores);
          setLoadingStores(false);
          setStoreError(null);
        },
        () => {
          setStoreError(<T>No pudimos cargar tus tiendas. Inténtalo más tarde.</T>);
          setLoadingStores(false);
        }
      );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (location.hash === `#${SECTION_ANCHOR_ID}`) {
      const target = document.getElementById(SECTION_ANCHOR_ID);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [location.hash]);

  return (
    <Box sx={{ bgcolor: brandColors.backgroundDefault, minHeight: "100vh", pb: "80px" }}>
      <StoresAppBar title={<T>Tiendas Notiline</T>} subtitle={<T>Guía y gestión</T>} quickNav={false} maxWidth="lg" />
      <Container maxWidth={false} disableGutters sx={{ ...pageContentSx, py: { xs: 6, md: 10 } }}>
        <Paper elevation={4} sx={{ p: { xs: 4, md: 6 }, borderRadius: 4, backgroundColor: brandColors.backgroundPaper, border: `1px solid ${brandColors.divider}` }}>
          <SectionHeader />

          <Grid container spacing={4}>
            {steps.map((step, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    height: "100%",
                    borderRadius: 3,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    backgroundColor: brandColors.backgroundPaper,
                    borderColor: "divider",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "center" }}>{step.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, textAlign: "center" }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center" }}>
                    {step.body}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: { xs: 5, md: 7 } }} />

          <Grid container spacing={3} sx={{ mb: { xs: 5, md: 8 } }}>
            {metrics.map((metric, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    p: 3,
                    height: "100%",
                    bgcolor: brandColors.backgroundPaper,
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    {metric.value}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {metric.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                    {metric.detail}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              mb: { xs: 5, md: 7 },
              bgcolor: brandColors.backgroundPaper,
              borderColor: "divider",
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Typography variant="overline" sx={{ color: brandColors.accent, letterSpacing: 1 }}>
                  <T>Beneficios clave</T>
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                  <T>Todo lo que necesitas para comenzar a vender</T>
                </Typography>
              </Grid>
              <Grid item xs={12} md={8}>
                <Stack spacing={2}>
                  {benefits.map((benefit, index) => (
                    <Paper key={index} elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: brandColors.backgroundPaper }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {benefit.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {benefit.body}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          <Box sx={{ mb: { xs: 5, md: 7 } }}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" mb={2}>
              <Box>
                <Typography variant="overline" sx={{ color: brandColors.accent, letterSpacing: 1 }}>
                  <T>Tus tiendas</T>
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  <T>Gestiona tus enlaces activos</T>
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={() => navigate("/stores/create")}
              >
                <T>Crear nueva tienda</T>
              </Button>
            </Stack>

            {storeError && <Alert severity="warning">{storeError}</Alert>}

            {loadingStores ? (
              <Grid container spacing={3}>
                {[1, 2].map((placeholder) => (
                  <Grid item xs={12} md={6} key={`store-skeleton-${placeholder}`}>
                    <Skeleton variant="rounded" height={140} />
                  </Grid>
                ))}
              </Grid>
            ) : stores.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, backgroundColor: brandColors.backgroundPaper, borderColor: "divider" }}>
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  <T>Aún no tienes tiendas creadas. Pulsa "Crear nueva tienda" para generar tu primer enlace público.</T>
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {stores.map((store) => (
                  <Grid item xs={12} md={6} key={store.id}>
                    <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, height: "100%", backgroundColor: brandColors.backgroundPaper, borderColor: "divider" }}>
                      <Stack spacing={1.5}>
                        <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                          {`notiline.com/stores/${store.slug}`}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {store.storeName}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary", flexGrow: 1 }}>
                          {store.description}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Button size="small" variant="contained" onClick={() => navigate(`/stores/${store.slug}`)}>
                            <T>Ver tienda</T>
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              navigate("/stores/create", {
                                state: {
                                  storeName: store.storeName,
                                  description: store.description,
                                  paypalEmail: store.paypalEmail || "",
                                  paypalClientId: store.paypalClientId || "",
                                  address: store.address || "",
                                  hours: store.hours || "",
                                  contactEmail: store.contactEmail || "",
                                  whatsapp: store.whatsapp || "",
                                  latitude: store.latitude,
                                  longitude: store.longitude,
                                },
                              })
                            }
                          >
                            <T>Editar</T>
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              justifyContent: "space-between",
              gap: 3,
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                <T>¿Listo para crear tu tienda?</T>
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                <T>Sigue el asistente paso a paso, publica tus primeros productos y comparte tu enlace
                con la comunidad Notiline.</T>
              </Typography>
            </Box>
            <Button
              size="large"
              variant="contained"
              color="primary"
              onClick={() => navigate("/stores/create")}
              sx={{
                flexShrink: 0,
                whiteSpace: "nowrap",
                fontWeight: 700,
                borderRadius: 999,
                boxShadow: "none",
                bgcolor: brandColors.accent,
                color: onAccent,
                "&:hover": { bgcolor: brandColors.accent, opacity: 0.88, boxShadow: "none" },
              }}
            >
              <T>Comenzar asistente</T>
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default StoresSection;
